export type ListItem = string | { text: string; url: string };

export type PillarCase = {
  title: string;
  paragraphs?: string[];
  punch?: string[];
  list?: ListItem[];
  chips?: string[];
  link?: { word: string; url: string };
};

export type Pillar = {
  num: string;
  id: string;
  title: string;
  desc: string;
  icon: string;
  cases: PillarCase[];
};

export const pillars: Pillar[] = [
  {
    num: "01",
    id: "pillar-01",
    title: "Erreichbar sein.",
    desc: "Kein Interessent wartet gerne. Kein Kunde ruft gerne zweimal an. Unsere Mitarbeiterinnen und Mitarbeiter sorgen dafür, dass Anrufe angenommen, Anliegen erfasst und Chancen nicht verpasst werden. Gerne auch in Kombination mit KI. Ganz so, wie Sie es sich für Ihre Kunden wünschen.",
    icon: "ri-chat-3-line",
    cases: [
      {
        title: "Wenn 4.000 Menschen am Tag Hilfe brauchen",
        paragraphs: [
          "Für eine große Versicherung übernahmen wir zeitweise mehrere tausend Anrufe täglich.",
        ],
        punch: [
          "Nicht weil es besonders spektakulär ist.",
          "Sondern weil jemand ans Telefon gehen musste.",
        ],
      },
      {
        title: "Wenn 1.600 Vertriebspartner Fragen haben",
        paragraphs: [
          "Für ein bundesweites Vertriebsnetz betreuen wir eine Support-Hotline, bearbeiten Tickets und unterstützen bei IT-Fragen.",
        ],
        punch: [
          "Denn manchmal entscheidet nicht die Software über die Zufriedenheit.",
          "Sondern der Mensch, der ans Telefon geht.",
        ],
      },
      {
        title: "Wenn Kunden wissen wollen, wie es weitergeht",
        paragraphs: [
          "Für ein großes Sanierungsunternehmen beantworten wir Anfragen von Kunden und Versicherungen. Außerdem unterstützen wir das Backoffice bei der Nachverfolgung laufender Schäden.",
        ],
        punch: ["Wer auf Antworten wartet, wartet nämlich selten gerne."],
      },
      {
        title: "Wenn Beschwerdemanagement wichtiger wird als Werbung",
        paragraphs: [
          "Ein Legal-Tech-Unternehmen unterstützen wir bei der Fallerfassung und bei der Betreuung der Hotline. Zusätzlich bearbeiten wir eingehende Beschwerden per E-Mail.",
        ],
        punch: [
          "Weil guter Service oft erst dann sichtbar wird, wenn etwas schiefgeht.",
        ],
      },
    ],
  },
  {
    num: "02",
    id: "pillar-02",
    title: "Chancen erkennen.",
    desc: "Aus Anrufen werden Termine. Aus Terminen werden Aufträge. Wir qualifizieren Anfragen, vereinbaren Termine, unterstützen Vertriebsprozesse und bereiten den nächsten Schritt professionell vor.",
    icon: "ri-eye-line",
    cases: [
      {
        title: "Gute Gespräche schlagen oft gute Werbung",
        paragraphs: [
          "Für einen großen Kaffeeröster vereinbaren wir bundesweit Termine für den Vertriebsaußendienst.",
        ],
        punch: [
          "Bis zu 40 Mal pro Woche entsteht aus einem Gespräch eine neue Chance.",
        ],
      },
      {
        title: "Wer fragt, hat oft echtes Interesse",
        paragraphs: [
          "Für einen Anbieter von Industriedampfsaugersystemen vereinbaren wir Vertriebstermine und bereiten Gespräche professionell vor.",
        ],
        punch: ["Aus Anfragen werden Termine.", "Aus Terminen entstehen Aufträge."],
      },
      {
        title: "Nicht jeder Lead kommt per Formular",
        paragraphs: [
          "Für einen Anbieter aus dem Energiesektor übernehmen wir die Terminvereinbarung für den Außendienst und laden Kunden zu Veranstaltungen ein.",
        ],
        punch: [
          "Denn manchmal beginnt eine Geschäftsbeziehung mit einem einzigen Telefonat.",
        ],
      },
      {
        title: "Wachstum findet oft im Kalender statt",
        paragraphs: [
          "Unsere Aufgabe ist nicht unbedingt, Produkte zu verkaufen.",
        ],
        punch: [
          "Unsere Aufgabe ist häufig, dafür zu sorgen, dass die richtigen Menschen miteinander sprechen.",
        ],
      },
    ],
  },
  {
    num: "03",
    id: "pillar-03",
    title: "Kunden gewinnen.",
    desc: "Von der ersten Kontaktaufnahme bis zur Auftragsabwicklung begleiten wir Ihre Kundenreise. Und wenn ein Prozess mehr braucht als Telefonie, unterstützen wir auch bei Backoffice, Fulfillment und Kundenservice.",
    icon: "ri-user-add-line",
    cases: [
      {
        title: "Manche Projekte wachsen mit",
        paragraphs: [
          "Was als telefonische Bestellannahme begann, entwickelte sich im Laufe der Zeit zu einem umfassenden Fulfillment-Projekt - handgepackt war geboren.",
        ],
        link: { word: "handgepackt", url: "https://www.handgepackt.de/" },
        punch: [
          "Heute gehören Lagerverwaltung, Versand und Retourenmanagement selbstverständlich dazu.",
        ],
      },
      {
        title: "Nicht jeder Anruf kommt von einem Kunden",
        paragraphs: [
          "Für einen Kunden betreiben wir den internen User-Helpdesk.",
          "Wenn Mitarbeitende Fragen zu CRM- oder Ticketsystemen haben, kümmern wir uns um die Antwort.",
        ],
        punch: ["Denn guter Service beginnt oft im eigenen Unternehmen."],
      },
      {
        title: "Kunden gewinnen ist nur der Anfang",
        paragraphs: ["Für verschiedene Auftraggeber begleiten wir die gesamte Reise:"],
        list: [
          "vom ersten Kontakt",
          "über die Terminvereinbarung",
          "bis zur laufenden Betreuung.",
        ],
        punch: [
          "Denn ein gewonnener Kunde ist wertvoll.",
          "Ein Kunde, der bleibt, noch viel mehr.",
        ],
      },
      {
        title: "Und manchmal helfen wir einfach dabei, dass alles läuft",
        list: ["Telefonie", "Vertrieb", "Support", "Prozesse", { text: "Fulfillment", url: "https://www.handgepackt.de/" }],
        paragraphs: [
          "Es gibt Projekte, die lassen sich nur schwer in eine Schublade stecken.",
        ],
        punch: ["Zum Glück müssen wir das auch nicht."],
      },
    ],
  },
];